import {
  Component,
  Input,
  OnInit,
  ViewChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  Release,
  ReviewClassification,
} from '../models/release';
import { EditReleaseDialogComponent } from '../dialog/edit-release/edit-release-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  GetRowIdFunc,
  GetRowIdParams,
  RowClassRules,
  ValueGetterParams,
} from 'ag-grid-enterprise';
import { themeQuartz } from 'ag-grid-community';
import { ReleaseSuccessFailureRenderer } from './release-success-failure-renderer';
import { ReviewClassificationDisplayPipe } from '../pipes/review-classification-display.pipe';
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

interface ReleaseStats {
  changeFailRatePipeline: number;
  failedPipelineReleases: number;
  totalReleases: number;
  failedReleases: number;
  changeFailRate: number;
  releasesByDate: Map<string, number>;
}

@Component({
  selector: 'app-releases-by-team',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, MatIconModule, AgGridAngular],
  template: `
    <h2>
      <mat-icon>info</mat-icon>
    BETA</h2>
    <div class="team-releases-container">
      <div class="stats-container">
        <div class="stats-card">
          <h3 class="stats-title">
            Release Statistics for {{ team }}
            @if (selectedPipeline) {
              <div class="selected-pipeline">
                {{ selectedPipeline }}
              </div>
            }
          </h3>
          <div class="stats-content">
            <div class="donut-charts-container">
              <div class="donut-chart-container">
                <canvas
                  baseChart
                  [data]="donutChartData"
                  [options]="donutChartOptions"
                  [type]="'doughnut'"
                  >
                </canvas>
                <div class="donut-center-text">
                  <div class="percentage">
                    {{ stats.changeFailRate | percent : '1.0-1' }}
                  </div>
                  <div class="label">Change Fail Rate</div>
                </div>
              </div>
              <div class="donut-chart-container">
                <canvas
                  baseChart
                  [data]="pipelineDonutChartData"
                  [options]="donutChartOptions"
                  [type]="'doughnut'"
                  >
                </canvas>
                <div class="donut-center-text">
                  <div class="percentage">
                    {{ stats.changeFailRatePipeline | percent : '1.0-1' }}
                  </div>
                  <div class="label">Pipeline Fail Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="chart-container">
          <canvas
            baseChart
            [data]="lineChartData"
            [options]="lineChartOptions"
            [type]="'line'"
            >
          </canvas>
        </div>
      </div>
    
      <div class="grid-container">
        <ag-grid-angular
          [ngClass]="{ 'ag-grid-angular': true }"
          [rowData]="teamReleases"
          [columnDefs]="columnDefs"
          [defaultColDef]="defaultColDef"
          [groupDefaultExpanded]="0"
          [animateRows]="true"
          [enableCellTextSelection]="true"
          [theme]="theme"
          [rowHeight]="48"
          [headerHeight]="48"
          [getRowId]="getRowId"
          [domLayout]="'autoHeight'"
          [groupDisplayType]="'groupRows'"
          (rowGroupOpened)="onRowGroupOpened($event)"
        ></ag-grid-angular>
      </div>
    </div>
    `,
  styles: [
    `
      h2 {
        color: #0093a1;
        margin-bottom: 16px;
        font-size: 1.2em;
        text-align: center;
      }

      .team-releases-container {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 16px;
        height: 100%;
      }

      .stats-container {
        display: flex;
        gap: 20px;
        height: 300px;
      }

      .stats-card {
        background: white;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        min-width: 450px;
        height: 100%;
        color: #333;
      }

      .stats-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: calc(100% - 40px);
      }

      .donut-charts-container {
        display: flex;
        gap: 24px;
        justify-content: center;
        width: 100%;
      }

      .donut-chart-container {
        position: relative;
        width: 180px;
        height: 180px;
        margin: 10px 0;
      }

      .donut-center-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        width: 100%;
      }

      .donut-center-text .percentage {
        font-size: 24px;
        font-weight: 500;
        color: #0093a1;
      }

      .donut-center-text .label {
        font-size: 12px;
        color: #666;
        margin-top: 4px;
      }

      .stats-title {
        color: #0093a1;
        margin-bottom: 16px;
        font-size: 1.2em;
        text-align: center;
      }

      .selected-pipeline {
        font-size: 0.9em;
        margin-top: 4px;
        color: #666;
      }

      .chart-container {
        flex: 1;
        background: white;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        height: 100%;
      }

      .grid-container {
        flex: 1;
        min-height: 400px;
        background: white;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        margin-top: 50px;
      }

      .ag-grid-angular {
        width: 100%;
        height: 100%;
      }

      :host ::ng-deep .ag-group-expanded,
      :host ::ng-deep .ag-group-contracted {
        color: #0093a1 !important;
      }
    `,
  ],
})
export class ReleasesByTeamComponent implements OnInit, OnChanges {
  @Input() team!: string;
  @Input() releases!: Release[];
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  public teamReleases: Release[] = [];
  public theme = themeQuartz;
  public stats: ReleaseStats = {
    totalReleases: 0,
    failedReleases: 0,
    changeFailRate: 0,
    releasesByDate: new Map(),
    changeFailRatePipeline: 0,
    failedPipelineReleases: 0,
  };

  public lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Releases per Day',
        borderColor: '#0093a1',
        tension: 0.1,
        fill: false,
      },
    ],
  };

  public lineChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Release Trend',
        color: '#333',
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#333',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#333',
        },
        grid: {
          color: '#e0e0e0',
        },
      },
      x: {
        ticks: {
          color: '#333',
        },
        grid: {
          color: '#e0e0e0',
        },
      },
    },
  };
  private readonly reviewClassificationPipe =
    new ReviewClassificationDisplayPipe();
  public columnDefs: ColDef[] = [
    {
      field: 'pipelineName',
      headerName: 'Pipeline',
      rowGroup: true,
      hide: true,
      showRowGroup: true,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      field: 'releaseStartedDate',
      headerName: 'Release Date',
      valueGetter: (params: ValueGetterParams) => {
        if (!params.data) return '';
        const date = new Date(params.data.releaseStartedDate);
        return date.toLocaleString();
      },
      sortable: true,
      filter: 'agDateColumnFilter',
      flex: 1.2,
    },
    {
      field: 'releaseName',
      headerName: 'Release Name',
      flex: 1,
      cellRenderer: (params: any) => {
        if (!params.data) return '';
        return ` <a
          style="text-decoration: underline; color: blue"
          target="_blank"
          href="${params.data.releaseLink}"
          rel="noopener noreferrer"
        >
          ${params.data.releaseName}
        </a>`;
      },
    },
    {
      field: 'branchName',
      headerName: 'Branch',
      flex: 1,
    },
    {
      field: 'releaseStatus',
      headerName: 'Status',
      cellRenderer: ReleaseSuccessFailureRenderer,
      cellRendererParams: {
        booleanPropertyName: 'releaseStatus',
      },
      flex: 0.8,
    },
    {
      field: 'changeFail',
      headerName: 'Change Fail',
      cellRenderer: ReleaseSuccessFailureRenderer,
      cellRendererParams: {
        booleanPropertyName: 'changeFail',
      },
      flex: 0.8,
    },
    {
      field: 'reviewClassification',
      headerName: 'Review',
      cellStyle: { cursor: 'pointer' },
      onCellClicked: (params: any) => this.openEditReleaseModal(params.data),
      flex: 0.8,
      cellRenderer: (params: any) => {
        const classification =
          ReviewClassification[
            params.value as keyof typeof ReviewClassification
          ];
        return `<div class="review-classification-cell">
          <i class="material-icons edit-icon" style="font-size: 18px; margin-right: 8px;color: #00a103">edit</i>
          <span class="review-text">
          ${this.reviewClassificationPipe.transform(classification)}
          </span>
        </div>`;
      },
    },
  ];

  public defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    autoHeight: true,
  };

  public rowClassRules: RowClassRules = {
    'highlight-row-red':
      'data.releaseStatus === false && data.reviewed === false',
    'highlight-row-green': (params: any) => {
      const release = params.data;
      return (
        (release?.releaseStatus === true && release?.reviewed === true) ||
        (release?.releaseStatus === false && release?.reviewed === true)
      );
    },
  };

  public getRowId: GetRowIdFunc = (params: GetRowIdParams) =>
    params.data.id?.toString() ?? '';

  public selectedPipeline: string | null = null;

  public donutChartData: ChartConfiguration<'doughnut'>['data'] = {
    labels: ['Successful', 'Failed'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#4caf50', '#f44336'],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  public donutChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const label = context.label ?? '';
            const value = context.parsed ?? 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  public pipelineDonutChartData: ChartConfiguration<'doughnut'>['data'] = {
    labels: ['Successful', 'Failed'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: ['#4caf50', '#f44336'],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  constructor(private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    this.calculateStats();
    if (this.agGrid?.api) {
      this.agGrid.api.sizeColumnsToFit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['releases'] || changes['team']) {
      console.log('Changes detected:', {
        team: this.team,
        releasesCount: this.releases?.length,
      });
      this.selectedPipeline = null;
      this.teamReleases = this.releases.filter((r) => r.team === this.team);
      this.calculateStats();
    }
  }

  private calculateStats(releases: Release[] = this.teamReleases): void {
    if (!releases?.length) {
      this.stats = {
        totalReleases: 0,
        failedReleases: 0,
        changeFailRate: 0,
        releasesByDate: new Map(),
        changeFailRatePipeline: 0,
        failedPipelineReleases: 0,
      };
      this.updateDonutChart();
      return;
    }

    this.stats.totalReleases = releases.length;
    this.stats.failedReleases = releases.filter((r) => !r.changeFail).length;
    this.stats.failedPipelineReleases = releases.filter(
      (r) => !r.releaseStatus
    ).length;
    this.stats.changeFailRate =
      this.stats.failedReleases / this.stats.totalReleases;
    this.stats.changeFailRatePipeline =
      this.stats.failedPipelineReleases / this.stats.totalReleases;

    // Update donut chart data
    this.updateDonutChart();

    // Group releases by date
    this.stats.releasesByDate.clear();
    releases
      .sort(
        (a, b) =>
          new Date(a.releaseStartedDate).getTime() -
          new Date(b.releaseStartedDate).getTime()
      )
      .forEach((release) => {
        const date = new Date(release.releaseStartedDate);
        const dateKey = date.toISOString().split('T')[0];
        const currentCount = this.stats.releasesByDate.get(dateKey) ?? 0;
        this.stats.releasesByDate.set(dateKey, currentCount + 1);
      });

    this.updateChart(releases);
  }

  private updateDonutChart(): void {
    const successful = this.stats.totalReleases - this.stats.failedReleases;
    const successfulPipeline =
      this.stats.totalReleases - this.stats.failedPipelineReleases;

    this.donutChartData.datasets[0].data = [
      successful,
      this.stats.failedReleases,
    ];

    this.pipelineDonutChartData.datasets[0].data = [
      successfulPipeline,
      this.stats.failedPipelineReleases,
    ];

    if (this.chart) {
      this.chart.update();
    }
  }

  private updateChart(releases: Release[] = this.teamReleases): void {
    if (!releases?.length) return;

    // Group releases by date and status
    const releasesByDate = new Map<
      string,
      { success: number; failed: number }
    >();

    releases
      .sort(
        (a, b) =>
          new Date(a.releaseStartedDate).getTime() -
          new Date(b.releaseStartedDate).getTime()
      )
      .forEach((release) => {
        const date = new Date(release.releaseStartedDate);
        const dateKey = date.toISOString().split('T')[0];
        const current = releasesByDate.get(dateKey) ?? {
          success: 0,
          failed: 0,
        };

        if (release.releaseStatus) {
          current.success++;
        } else {
          current.failed++;
        }

        releasesByDate.set(dateKey, current);
      });

    // Sort dates and prepare chart data
    const sortedEntries = Array.from(releasesByDate.entries()).sort(
      ([dateA], [dateB]) => dateA.localeCompare(dateB)
    );

    const labels = sortedEntries.map(([date]) => {
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year.slice(2)}`;
    });

    const chartData = {
      labels,
      datasets: [
        {
          data: sortedEntries.map(([_, counts]) => counts.success),
          label: 'Successful Releases',
          borderColor: '#0093a1',
          backgroundColor: 'rgba(0, 147, 161, 0.1)',
          tension: 0.1,
          fill: true,
          pointBackgroundColor: '#0093a1',
        },
        {
          data: sortedEntries.map(([_, counts]) => counts.failed),
          label: 'Failed Releases',
          borderColor: '#dc3545',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          tension: 0.1,
          fill: true,
          pointBackgroundColor: '#dc3545',
        },
      ],
    };

    console.log('Chart data:', chartData);

    this.lineChartData = chartData;
    this.chart?.update();
  }

  private openEditReleaseModal(release: Release): void {
    const dialogRef = this.dialog.open(EditReleaseDialogComponent, {
      width: '600px',
      panelClass: 'release-edit-dialog',
      data: { ...release },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const index = this.teamReleases.findIndex(
          (item) => item.id === result.id
        );
        if (index > -1) {
          this.teamReleases[index] = result;
          const rowNode = this.agGrid.api.getRowNode(result.id!.toString());
          if (rowNode) {
            rowNode.setData(result);
            this.calculateStats();
            this.agGrid.api.refreshCells({ force: true });
          }
        }
      }
    });
  }

  ngAfterViewInit() {
    if (this.agGrid?.api) {
      this.agGrid.api.sizeColumnsToFit();
    }
  }

  public onRowGroupOpened(event: any): void {
    if (!event.node.group) return;

    const pipeline = event.node.key;
    if (event.node.expanded) {
      // Group was expanded
      this.selectedPipeline = pipeline;
      const pipelineReleases = this.teamReleases.filter(
        (r) => r.pipelineName === pipeline
      );
      this.calculateStats(pipelineReleases);
    } else {
      // Group was collapsed
      this.selectedPipeline = null;
      this.calculateStats(this.teamReleases);
    }
  }
}
